import { useEffect, useCallback, useRef, useState } from 'react';

// Services
import {
  createWebSocketService,
  type WebSocketService,
  type WebSocketConfig,
  type WebSocketEventHandler,
  type WebSocketMessage,
} from '@/services/websocket';

// Types

// Config
import { WEBSOCKET_CONFIG } from '@/constants/config';
import { BASE_URL_ENDPOINT } from '@/constants';

// Providers
import { useFirebase } from '@/providers/firebase';

// Utils
import { getWebSocketUrl } from '@/utils/endpoint';

// Constants

// Providers
import { useWebSocketContext } from '@/providers';

export interface UseWebSocketOptions {
  url: string;
  autoConnect?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onMessage?: (message?: WebSocketMessage) => void;
}

export const useWebSocket = (options: UseWebSocketOptions) => {
  const {
    url,
    autoConnect = true,
    onConnect,
    onDisconnect,
    onError,
    onMessage,
  } = options || {};
  const [isConnected, setIsConnected] = useState(false);
  const { getToken, isLoading } = useFirebase();

  const serviceRef = useRef<WebSocketService | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Create WebSocket URL from API endpoint
  const formatWebSocketUrl = useCallback(async () => {
    const token = await getToken();

    if (!token) return;

    // Convert HTTP(S) to WS(S)
    const wsUrl = getWebSocketUrl(url);

    return `${wsUrl}${BASE_URL_ENDPOINT}/ws?token=${token}`;
  }, [url, getToken]);

  // Handle WebSocket messages
  const handleMessage = useCallback<WebSocketEventHandler>(
    (message) => {
      onMessage?.(message);
    },
    [onMessage],
  );

  const createService = useCallback(() => {
    const { MAX_RECONNECT_ATTEMPTS, RECONNECT_INTERVAL } = WEBSOCKET_CONFIG;

    const config: WebSocketConfig = {
      reconnectInterval: RECONNECT_INTERVAL,
      maxReconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      resolveUrl: formatWebSocketUrl,
      onOpen: () => {
        setIsConnected(true);
        onConnect?.();
      },
      onClose: () => {
        setIsConnected(false);
        onDisconnect?.();
      },
      onError,
    };

    const service = createWebSocketService(config);

    unsubscribeRef.current = service.subscribe(handleMessage);
    serviceRef.current = service;

    return service;
  }, [formatWebSocketUrl, handleMessage, onConnect, onDisconnect, onError]);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    try {
      const service = serviceRef.current ?? createService();

      await service.connect();
      setIsConnected(service.isSocketConnected());
    } catch (error) {
      if (!serviceRef.current) {
        onError?.(error as Event);
      }
    }
  }, [createService, onError]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (serviceRef.current) {
      serviceRef.current.disconnect();
      serviceRef.current = null;
    }

    setIsConnected(false);
    onDisconnect?.();
  }, [onDisconnect]);

  // Send message
  const send = useCallback(
    (message: Record<string, unknown>) => {
      if (isConnected && serviceRef.current) {
        serviceRef.current.send(message);
      }
    },
    [isConnected],
  );

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect && !isLoading) {
      connect().catch(() => {}); // Silently ignore initial connection failures
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect, isLoading]);

  useEffect(() => {
    if (!autoConnect) {
      return;
    }

    const reconnectIfNeeded = () => {
      if (document.visibilityState === 'hidden' || isLoading) {
        return;
      }

      if (!serviceRef.current?.isSocketConnected()) {
        connect().catch(() => {});
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        reconnectIfNeeded();
      }
    };

    window.addEventListener('focus', reconnectIfNeeded);
    window.addEventListener('online', reconnectIfNeeded);
    window.addEventListener('pageshow', reconnectIfNeeded);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', reconnectIfNeeded);
      window.removeEventListener('online', reconnectIfNeeded);
      window.removeEventListener('pageshow', reconnectIfNeeded);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoConnect, connect, isLoading]);

  return {
    connect,
    disconnect,
    isConnected,
    send,
  };
};

export const useWebSocketMessage = <T = unknown>(
  callback: (message?: WebSocketMessage<T>) => void,
  deps: React.DependencyList = [],
  url?: string,
) => {
  const { onMessage, connectedUrls } = useWebSocketContext();

  // Check if the URL is in the connected URLs list
  const isUrlConnected = url
    ? connectedUrls.includes(url)
    : connectedUrls.length > 0;

  useEffect(() => {
    // Only subscribe to messages when connected
    if (!isUrlConnected) {
      return;
    }

    const unsubscribe = onMessage(
      callback as (message?: WebSocketMessage) => void,
      url,
    );

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMessage, callback, url, isUrlConnected, ...deps]);
};
